/**
 * Socket reconnection policy.
 *
 * Extracted from WebSocketContext so the rules are testable: the interesting
 * cases here are all ones that only show up on teardown or during a race, which
 * is exactly where an inline handler stops being reviewable.
 */

export const MAX_RECONNECT_DELAY_MS = 30000;

/**
 * Should a close event from `closingSocket` be acted on at all?
 *
 * Only the socket the provider currently considers live may drive state. A
 * close from any other socket is either an explicit disconnect (which clears
 * the ref before the event arrives) or a socket that has already been
 * superseded — in both cases its close must not reset connection state that now
 * belongs to a newer socket, and must not schedule a reconnect.
 */
export function isLiveSocketClose(currentSocket, closingSocket) {
  return Boolean(closingSocket) && currentSocket === closingSocket;
}

/**
 * After a live socket closes, should we try to reconnect?
 *
 * Local sessions deliberately hold no socket, so reconnecting would spin a
 * timer against a connect() that immediately returns.
 */
export function shouldReconnect({ isLocalMode }) {
  return !isLocalMode;
}

/** Exponential backoff, capped: 1s, 2s, 4s, 8s, 16s, 30s, 30s… */
export function reconnectDelayMs(attempts, maxDelayMs = MAX_RECONNECT_DELAY_MS) {
  const safeAttempts = Number.isFinite(attempts) && attempts > 0 ? Math.floor(attempts) : 0;
  // Cap the exponent before Math.pow so a long-lived tab cannot reach Infinity.
  const exponent = Math.min(safeAttempts, 32);
  return Math.min(1000 * Math.pow(2, exponent), maxDelayMs);
}
